import { captureMessage } from "@sentry/react";
import { useState } from "react";
import { TbDeviceMobile, TbSearch } from "react-icons/tb";
import { Controller, useForm } from "react-hook-form";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { Alert, Box, Button, Stack, TextInput, rem } from "@mantine/core";
import type { InferInput } from "valibot";
import { minLength, object, pipe, string } from "valibot";

import { LookupResult } from "./lookup-result";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Number Lookup Form Values Schema
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const NumberLookupFormValuesSchema = object({
  msisdn: pipe(string(), minLength(1, "Please enter MSISDN")),
});

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Number Lookup Form Values
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

type NumberLookupFormValues = InferInput<typeof NumberLookupFormValuesSchema>;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Number Lookup Form
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

type NumberLookupFormProps = {
  readonly apiKey: string;
  readonly defaultValues?: Partial<NumberLookupFormValues>;
};

export function NumberLookupForm({
  apiKey,
  defaultValues,
}: NumberLookupFormProps) {
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<object[] | null>(null);

  const { handleSubmit, formState, control } = useForm<NumberLookupFormValues>({
    resolver: valibotResolver(NumberLookupFormValuesSchema),
    defaultValues: {
      msisdn: defaultValues?.msisdn ?? "",
    },
  });

  const { isSubmitting, submitCount, isValid, errors } = formState;

  const isFormInvalid = Object.keys(errors).length !== 0 || !isValid;
  const isSubmitDisabled = (submitCount > 0 && isFormInvalid) || isSubmitting;

  // TODO: THIS IS NOT THE BEST WAY TO CHECK FOR INVALID NUMBER

  // const isInvalidNumber =
  //   lookupResult &&
  //   Object.keys(lookupResult[0]).length === 3 &&
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //   // @ts-ignore-next-line
  //   lookupResult[0].date &&
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //   // @ts-ignore-next-line
  //   lookupResult[0].msisdn &&
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //   // @ts-ignore-next-line
  //   (Number(lookupResult[0].response) === 0 ||
  //     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //     // @ts-ignore-next-line
  //     Number(lookupResult[0].response) === 5);

  return (
    <Stack gap="xl" align="center" w="100%">
      <Box w="100%" miw={rem(380)} maw={rem(480)}>
        <Stack gap="xl" w="100%">
          <Box
            component="form"
            noValidate={true}
            onSubmit={handleSubmit(async (values) => {
              setLookupError(null);

              try {
                const apiUrl = new URL(__NUMBER_LOOKUP_API_URL__);
                apiUrl.searchParams.set("key", apiKey);
                apiUrl.searchParams.set("msisdn", values.msisdn);

                const response = await fetch(apiUrl.toString());

                if (response.status === 401) {
                  return setLookupError("Invalid API Key!");
                }

                if (!response.ok) {
                  captureMessage(
                    `Number Lookup API Response Error: ${response.statusText}`,
                    { level: "fatal" },
                  );

                  return setLookupError(response.statusText);
                }

                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                return setLookupResult(await response.json());
              } catch (error) {
                if (error instanceof Error) {
                  captureMessage(
                    `Number Lookup Request Error: ${error.message}`,
                    { level: "fatal" },
                  );

                  return setLookupError(error.message);
                }

                captureMessage("Number Lookup Request Error: Unknown Error", {
                  level: "fatal",
                });

                return setLookupError("Something went wrong!");
              }
            })}
          >
            <Stack>
              <Controller
                control={control}
                name="msisdn"
                render={({ field, fieldState: { error } }) => (
                  <TextInput
                    {...field}
                    leftSection={<TbDeviceMobile />}
                    label="MSISDN"
                    description="Enter MSISDN you want to lookup"
                    placeholder="Enter MSISDN you want to lookup"
                    error={error?.message}
                    required={true}
                    readOnly={isSubmitting}
                  />
                )}
              />

              <Button
                type="submit"
                fullWidth={true}
                leftSection={<TbSearch />}
                disabled={isSubmitDisabled}
                loading={isSubmitting}
              >
                Number Lookup
              </Button>
            </Stack>
          </Box>

          {/* {isInvalidNumber && (
            <Alert variant="filled" color="red" ta="center" fw="bold" w="100%">
              Invalid Number
            </Alert>
          )} */}

          {/* {!isInvalidNumber && lookupError && ( */}
          {lookupError && (
            <Alert variant="filled" color="red" ta="center" fw="bold" w="100%">
              {lookupError}
            </Alert>
          )}
        </Stack>
      </Box>

      {/* {!isInvalidNumber && !lookupError && lookupResult && ( */}
      {!lookupError && lookupResult && (
        <LookupResult isLoading={isSubmitting} lookupResult={lookupResult} />
      )}
    </Stack>
  );
}
